/**
 * SchemaDeliveryCard
 *
 * Honest delivery guidance for approved schema.org fixes.
 *
 * The default beacon tag injects schema client-side via JavaScript. The AI
 * crawlers for ChatGPT, Perplexity and Claude do not execute JavaScript
 * (Vercel × MERJ, 500M+ fetches), so they never see it. To be cited by those
 * engines, the JSON-LD must be in the page's raw HTML — pasted server-side into
 * <head>, or delivered by the edge proxy.
 *
 * This card surfaces that gap plainly and hands the customer the exact block to
 * paste. It is intentionally not alarmist about the beacon tag (Google/Gemini
 * do render it) — it frames server-side placement as the way to reach the
 * non-rendering engines.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';

export function SchemaDeliveryCard({ domainId }: { domainId: string }) {
  const [copied, setCopied] = useState(false);

  const { data: jsonld, isLoading, isError } = useQuery({
    queryKey: ['schema-jsonld', domainId],
    queryFn: () => api.cdn.schemaJsonld(domainId),
    staleTime: 30_000,
    retry: 1,
  });

  // No approved schema yet → the endpoint returns "{}". Nothing to deliver.
  const hasSchema = !!jsonld && jsonld.trim() !== '' && jsonld.trim() !== '{}';

  const snippet = hasSchema
    ? `<script type="application/ld+json">\n${jsonld!.trim()}\n</script>`
    : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — the block is selectable in the <pre> as a fallback.
    }
  };

  if (isLoading || isError || !hasSchema) return null;

  return (
    <div className="bg-white rounded-[6px] p-6 border border-amber-200">
      <div className="flex items-start gap-2.5 mb-3">
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" className="text-amber-600 mt-0.5 shrink-0" aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
        <div>
          <p className="text-sm font-semibold text-[#0F172A]">Reach ChatGPT, Perplexity &amp; Claude</p>
          <p className="text-xs text-[#64748B] mt-1 leading-relaxed">
            Your approved schema is delivered by the fixmyseo tag, which runs in the browser.
            Google and Gemini render it, but the crawlers behind <strong>ChatGPT, Perplexity and
            Claude don&apos;t execute JavaScript</strong> — so they only see structured data that&apos;s
            in your page&apos;s raw HTML. Paste this block into your site&apos;s <code>&lt;head&gt;</code> (server-side)
            to be visible to every AI engine. Re-copy it whenever you approve new schema.
          </p>
        </div>
      </div>

      <div className="relative">
        <pre className="text-[11px] leading-relaxed bg-[#0F172A] text-[#E2E8F0] rounded-[6px] p-4 overflow-x-auto max-h-64">
          {snippet}
        </pre>
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 text-[11px] font-medium px-2.5 py-1 rounded-[4px] bg-white/10 text-white hover:bg-white/20 transition-colors"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <p className="text-[11px] text-[#94A3B8] mt-2">
        Prefer automatic delivery? The WordPress plugin and edge proxy both place this in your raw
        HTML for you — no re-pasting.
      </p>
    </div>
  );
}
